import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import NewItemDialog from "../modal/NewItemDialog";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import { Input } from "../ui/input";
import { isAxiosError } from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "@/hooks/use-toast";
import { useCreateAuthor } from "@/api/author/author";
import { authorSchema } from "@/api/author/author-validation";

const schema = authorSchema.pick({
  artistesAuteursDescriptionNom: true,
  artistesAuteursDescriptionPrenom: true,
});

type FormType = z.infer<typeof schema>;

type NewAuthorDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

const NewAuthorDialog = ({ onOpenChange, open }: NewAuthorDialogProps) => {
  const { t } = useTranslation();
  const form = useForm<FormType>({
    resolver: zodResolver(schema),
    defaultValues: {
      artistesAuteursDescriptionPrenom: "",
      artistesAuteursDescriptionNom: "",
    },
  });
  const { mutateAsync } = useCreateAuthor();
  const navigate = useNavigate();

  const onFormSubmit = async (data: FormType, shouldRedirect: boolean) => {
    try {
      const result = await mutateAsync(data);
      if (!shouldRedirect) {
        toast({
          title: t("newRecordSuccessfullyCreated"),
          duration: 3000,
          variant: "success",
        });
        form.reset();
        return;
      }
      navigate(`/edit/author/${result.id}`);
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 409) {
        form.setError("artistesAuteursDescriptionNom", {
          message: t("itemWithSameIdAlreadyExists"),
        });
        return;
      }
      form.setError("root", { message: t("unknownError") });
      throw err;
    }
  };

  return (
    <NewItemDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("createAauthor")}
      onFormSubmit={onFormSubmit}
      form={form}
    >
      <FormField
        control={form.control}
        name="artistesAuteursDescriptionPrenom"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t("artistesAuteursDescriptionPrenom")}</FormLabel>
            <FormControl>
              <Input {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="artistesAuteursDescriptionNom"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t("artistesAuteursDescriptionNom")}</FormLabel>
            <FormControl>
              <Input {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormMessage>{form.formState.errors.root?.message}</FormMessage>
    </NewItemDialog>
  );
};

export default NewAuthorDialog;
